import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, CheckCircle2, XCircle, Camera, CameraOff, Loader2, Trophy, Users, Ticket, Clock, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

// Phase 87 — Coach-side Event Check-In Scanner.
// Uses phone camera (front/back) to scan TKT-XXX-YYY QR codes and stamp
// checked_in_at on the matching registration. Idempotent, with audible/visual
// feedback and a live roll-call.
//
// Live counters refresh after every successful scan; manual code entry is
// always available as a fallback when the camera is blocked or QR is damaged.

const SCAN_COOLDOWN_MS = 1800; // debounce duplicate detections of the same ticket

export default function EventCheckIn() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {type: success|error|already, ...}
  const [manualCode, setManualCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const qrRef = useRef(null);
  const lastScanRef = useRef({ code: null, t: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get(`/events/${eventId}/checkin-stats`);
      setStats(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load event');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Submit a scanned (or manually-entered) code to the backend.
  const submitCode = useCallback(async (rawCode) => {
    if (submitting) return;
    const code = (rawCode || '').trim();
    if (!code) return;
    // Debounce: same code within cooldown is ignored to prevent burst-firing
    // when the camera detects the QR for 30+ frames in a row.
    const now = Date.now();
    if (lastScanRef.current.code === code && now - lastScanRef.current.t < SCAN_COOLDOWN_MS) {
      return;
    }
    lastScanRef.current = { code, t: now };
    setSubmitting(true);
    try {
      const r = await api.post(`/events/${eventId}/checkin`, { qr_payload: code });
      if (r.data.already_checked_in) {
        setLastResult({ type: 'already', ...r.data });
        toast.info(`${r.data.participant_name} was already checked in`);
      } else {
        setLastResult({ type: 'success', ...r.data });
        toast.success(`${r.data.participant_name} — checked in! ✓`);
        try { navigator.vibrate?.(120); } catch {}
      }
      // Live-refresh the counters & recent roll-call
      fetchStats();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Scan failed';
      setLastResult({ type: 'error', message: detail, ticket_code: code });
      toast.error(detail);
      try { navigator.vibrate?.([60, 40, 60]); } catch {}
    } finally {
      setSubmitting(false);
    }
  }, [eventId, fetchStats, submitting]);

  // Start/stop the camera scanner via html5-qrcode.
  const startScanner = useCallback(async () => {
    if (scanning) return;
    try {
      if (!qrRef.current) {
        qrRef.current = new Html5Qrcode('outplay-qr-reader', {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        });
      }
      await qrRef.current.start(
        { facingMode: 'environment' },
        { fps: 12, qrbox: { width: 240, height: 240 } },
        (decodedText) => { submitCode(decodedText); },
        () => { /* swallow per-frame decode errors */ }
      );
      setScanning(true);
    } catch (err) {
      toast.error('Camera permission denied or unavailable. Use manual entry below.');
    }
  }, [scanning, submitCode]);

  const stopScanner = useCallback(async () => {
    try {
      if (qrRef.current && qrRef.current.isScanning) {
        await qrRef.current.stop();
        await qrRef.current.clear();
      }
    } catch {}
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => { stopScanner(); };
  }, [stopScanner]);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    await submitCode(manualCode);
    setManualCode('');
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }
  if (!stats) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Event not found.</div>;
  }

  const { event, total, checked_in, pending, recent } = stats;
  const pct = total > 0 ? Math.round((checked_in / total) * 100) : 0;

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4" data-testid="event-checkin-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/events')} className="-ml-2" data-testid="checkin-back">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to Events
      </Button>

      {/* Header: event + live counters */}
      <Card className="bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-800 text-white border-0 overflow-hidden">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold opacity-80 flex items-center gap-1">
                <Trophy className="h-3 w-3" /> Matchday Check-In
              </p>
              <h1 className="text-lg font-display font-bold leading-tight mt-0.5" data-testid="checkin-event-title">{event.title}</h1>
              <p className="text-[11px] opacity-80 mt-0.5">{event.date}{event.venue ? ` · ${event.venue}` : ''}</p>
            </div>
            <Badge className="bg-white/20 hover:bg-white/30 text-white border-0 text-[10px]">{pct}%</Badge>
          </div>
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-white/10">
            <Stat label="Registered" value={total} icon={<Users className="h-3 w-3" />} testId="checkin-stat-total" />
            <Stat label="Checked-In" value={checked_in} icon={<CheckCircle2 className="h-3 w-3" />} testId="checkin-stat-in" />
            <Stat label="Pending" value={pending} icon={<Clock className="h-3 w-3" />} testId="checkin-stat-pending" />
          </div>
        </CardContent>
      </Card>

      {/* Scanner */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">Scan ticket QR</p>
              <p className="text-[11px] text-muted-foreground">Point the back camera at the participant's ticket.</p>
            </div>
            {scanning ? (
              <Button size="sm" variant="outline" onClick={stopScanner} data-testid="checkin-stop-camera">
                <CameraOff className="h-3.5 w-3.5 mr-1" /> Stop
              </Button>
            ) : (
              <Button size="sm" onClick={startScanner} className="bg-emerald-600 hover:bg-emerald-700" data-testid="checkin-start-camera">
                <Camera className="h-3.5 w-3.5 mr-1" /> Start Camera
              </Button>
            )}
          </div>

          {/* Scanner viewport — html5-qrcode mounts here when scanning */}
          <div
            id="outplay-qr-reader"
            data-testid="checkin-qr-reader"
            className={`rounded-xl overflow-hidden bg-slate-950 ${scanning ? 'block' : 'hidden'}`}
            style={{ minHeight: scanning ? 280 : 0 }}
          />
          {!scanning && (
            <div className="rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 p-10 text-center" data-testid="checkin-camera-idle">
              <Camera className="h-10 w-10 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Camera is off. Tap <span className="font-semibold">Start Camera</span> to begin scanning.</p>
            </div>
          )}

          {/* Manual entry fallback */}
          <form onSubmit={handleManualSubmit} className="flex gap-2 pt-2 border-t" data-testid="checkin-manual-form">
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Or type code: TKT-XXXX-XXXX"
              className="h-9 font-mono text-xs uppercase"
              data-testid="checkin-manual-input"
            />
            <Button type="submit" size="sm" disabled={submitting || !manualCode.trim()} data-testid="checkin-manual-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Last scan result */}
      {lastResult && <ScanResultCard result={lastResult} eventId={eventId} />}

      {/* Recent check-ins roll-call */}
      <Card data-testid="checkin-rollcall">
        <CardContent className="p-4 space-y-2">
          <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Recent check-ins ({recent.length})</p>
          {recent.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No one's checked in yet.</p>
          ) : (
            <div className="space-y-1.5">
              {recent.map((r) => (
                <div key={r.ticket_code} className="flex items-center justify-between text-xs p-2 rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20" data-testid={`rollcall-row-${r.ticket_code}`}>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{r.participant_name}</p>
                    {r.team_name && <p className="text-[10px] text-muted-foreground truncate">{r.team_name}</p>}
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]"><CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> In</Badge>
                    <p className="text-[9px] text-muted-foreground mt-0.5 tabular-nums">{formatTime(r.checked_in_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, icon, testId }) {
  return (
    <div className="text-center" data-testid={testId}>
      <p className="text-[9px] uppercase tracking-wider opacity-70 flex items-center justify-center gap-0.5">{icon} {label}</p>
      <p className="text-xl font-display font-bold tabular-nums">{value}</p>
    </div>
  );
}

function ScanResultCard({ result, eventId }) {
  const gatePassUrl = result.registration_id
    ? `/events/${eventId}/registrations/${result.registration_id}/gatepass`
    : null;
  if (result.type === 'success') {
    return (
      <Card className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="checkin-result-success">
        <CardContent className="p-4 flex items-center gap-3">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Checked In</p>
            <p className="font-semibold truncate">{result.participant_name}</p>
            <p className="text-[11px] text-muted-foreground truncate">{result.team_name || 'No team'} · <span className="font-mono">{result.ticket_code}</span></p>
          </div>
          {gatePassUrl && (
            <Button size="sm" variant="outline" className="shrink-0 h-8" onClick={() => window.open(gatePassUrl, '_blank')} data-testid="checkin-print-gatepass">
              <Printer className="h-3.5 w-3.5 mr-1" /> Pass
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }
  if (result.type === 'already') {
    return (
      <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/30" data-testid="checkin-result-already">
        <CardContent className="p-4 flex items-center gap-3">
          <Clock className="h-10 w-10 text-amber-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Already Checked-In</p>
            <p className="font-semibold truncate">{result.participant_name}</p>
            <p className="text-[11px] text-muted-foreground truncate">Originally at {formatTime(result.checked_in_at)}</p>
          </div>
          {gatePassUrl && (
            <Button size="sm" variant="outline" className="shrink-0 h-8" onClick={() => window.open(gatePassUrl, '_blank')} data-testid="checkin-print-gatepass-already">
              <Printer className="h-3.5 w-3.5 mr-1" /> Pass
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-red-300 bg-red-50 dark:bg-red-950/30" data-testid="checkin-result-error">
      <CardContent className="p-4 flex items-center gap-3">
        <XCircle className="h-10 w-10 text-red-600 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Scan Failed</p>
          <p className="font-semibold text-sm">{result.message}</p>
          {result.ticket_code && <p className="text-[11px] text-muted-foreground font-mono truncate">{result.ticket_code}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}
